import React, { useEffect, useRef, useState } from 'react'
import mapboxgl from '!mapbox-gl' // eslint-disable-line import/no-webpack-loader-syntax
import { useSelector, useDispatch } from 'react-redux'
import { Outlet, useLocation } from 'react-router-dom'
import { Box, Stack, CircularProgress } from '@mui/material'
import { PinDrop } from '@mui/icons-material'
import 'mapbox-gl/dist/mapbox-gl.css'
import './mapbox-gl.css'
import _ from 'lodash'
import styled from '@emotion/styled'
import { getAllLocations } from '../../services'
import { features as setFeature } from '../../redux/slices/Tree/treeFeatures'
import { setFilterState } from '../../redux/slices/Search/input'
import { setMarker, changePopup } from '../../redux/slices/Map/map'
import { show as openDrawerLeft } from '../../redux/slices/Drawer/drawerLeft'
import LinearInderterminate from '../LinearIndeterminate'
import ContentOptions from '../ContentOptions'
import PopupContent from '../PopupContent'
import PopupContentContainer from '../PopupContentContainer'
import { setEnableButton } from '../../redux/slices/Modal/enableButton'
import ContentMoveMarker from '../ContentMoveMarker'
import * as rt from '../../config/routeType'
import Botton from '../BottonCustom'
import AlertCustom from '../Alerts'
import { globalMapboxSettings as mbgls } from '../../utils'
import LoadingData from './modal'

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN

const COLOR_PRIMARY = 'var(--color-primary)'
const COLOR_WHITE = '#fffafa'
const COLOR_GREEN = '#a8f446'

const registerRute = `/${rt.MANAGEMENT_TREE}/${rt.TREE}/${rt.REGISTER_TREE}`
const mainRute = `${rt.MAIN}${rt.TREE}`

const MapContainer = styled.div`
  width: ${props => props.widthMap ?? '100%'};
  height: 100%;
`
const contentStyle = {
  width: '100%',
  height: '100%',
  textAlign: 'center',
}

const styleBottonContainer = {
  width: 150,
  backgroundColor: '#fff',
  position: 'absolute',
  zIndex: 1,
  bottom: '42px',
  left: 'calc(50% - 75px)',
}

const styleDefaultBotton = {
  color: COLOR_PRIMARY,
  backgroundColor: COLOR_WHITE,
  position: 'absolute',
  border: `1px solid ${COLOR_WHITE}`,
  width: '100%',
  fontSize: '12px',
  '&:hover': {
    backgroundColor: COLOR_WHITE,
    color: 'var(--color-green)',
  },
  '&:active': { boxShadow: 'none', opacity: 0.9 },
}

const Map = ({ widthMap, minimap = false, allOption, isAuth, coord }) => {
  const dispatch = useDispatch()
  let location = useLocation()
  const geolocate = useRef(null)
  const mapContainer = useRef(null)
  const mapTree = useRef(null)
  const markerTree = React.useRef(null)
  const alertRef = React.useRef()
  const isntTreeId = React.useRef()
  const zoomLocal = window.localStorage.getItem('arbolQR_Map_Zoom')
  const lngLocal = window.localStorage.getItem('arbolQR_Map_lng')
  const latLocal = window.localStorage.getItem('arbolQR_Map_lat')

  // Map
  const [lng, setLng] = useState(lngLocal ?? mbgls.lng)
  const [lat, setLat] = useState(latLocal ?? mbgls.lat)
  const [zoom, setZoom] = useState(zoomLocal ?? mbgls.zoom)

  // PopupContent
  const [content, setContent] = useState([])
  const [popupLngLat, setPopupLngLat] = useState(null)
  // Marker
  const [lngLatNew, setLngLatNew] = React.useState(null)
  const [propsContent, setPropsContent] = React.useState(null)

  // Loading data tree
  const [loading, setLoading] = useState(false)
  const [timeoutReached, setTimeoutReached] = useState(false)

  const _state = useSelector(state => state.drawerLeft)
  const _initialState = useSelector(state => state.treeFeatures)
  const _styleMap = useSelector(state => state.mapState.style.current)
  const markerStatus = useSelector(state => state.mapState.marker)
  const popupOpen = useSelector(state => state.mapState.popupOpen)
  const filter = useSelector(state => state.filterSearch.filter)
  const activeAlert = useSelector(state => state.alertsGlobal.isStored)

  const styleBoxContainerMap = {
    ml: _state.open ? _state.drawerWidthMin : 0,
    mt: _state.drawerHeight,
    width: `calc(calc(100vw - ${_state.drawerWidthMin}) + 1px)`,
    height: _state.contentBox,
    display: 'flex',
    flexDirection: 'row',
  }

  function addSource(id, geojson) {
    mapTree.current.addSource(id, {
      type: 'geojson',
      data: geojson,
      cluster: true,
      clusterMaxZoom: 13, // Max zoom to cluster points on
      clusterRadius: 40,
    })
  }

  function addLayer(id) {
    mapTree.current.addLayer({
      id: 'clusters',
      type: 'circle',
      source: id,
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': [
          'step',
          ['get', 'point_count'],
          COLOR_GREEN,
          100,
          COLOR_GREEN,
          750,
          COLOR_GREEN,
        ],
        'circle-radius': ['step', ['get', 'point_count'], 20, 100, 30, 750, 40],
        'circle-stroke-width': 2,
        'circle-stroke-color': '#ffffff',
      },
    })
    mapTree.current.addLayer({
      id: 'cluster-count',
      type: 'symbol',
      source: id,
      filter: ['has', 'point_count'],
      layout: {
        'text-field': ['get', 'point_count_abbreviated'],
        'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
        'text-size': 12,
      },
    })

    mapTree.current.addLayer({
      id: 'unclustered-point',
      type: 'circle',
      source: id,
      filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-radius': {
          base: 8,
          stops: [
            [16, 5],
            [22, 19],
          ],
        },
        'circle-opacity': 0.75,
        'circle-stroke-width': 1,
      },
    })

    // Change color circles
    mapTree.current.setPaintProperty(
      'unclustered-point',
      'circle-color',
      isAuth
        ? {
            property: 'treeVerified',
            type: 'categorical',
            stops: [
              ['Y', '#00ffff'],
              ['N', '#DC143C'],
              ['LE', '#fbb03b'],
            ],
          }
        : COLOR_GREEN
    )

    // Change color line of circle
    mapTree.current.setPaintProperty('unclustered-point', 'circle-stroke-color', {
      property: 'treeVerified',
      type: 'categorical',
      stops: _.startsWith(location.pathname, `${rt.MAIN}${rt.TREE}`)
        ? [
            ['Y', '#000'],
            ['N', '#191970'],
            ['LE', '#000'],
          ]
        : [
            ['Y', '#0000FF'],
            ['N', '#fdefef'],
            ['LE', '#ffff00'],
          ],
    })
  }

  const handleTreeLocation = () => {
    setPopupLngLat(lngLatNew)
    markerTree.current.setDraggable(false)
    dispatch(
      setMarker({
        openMarker: true,
        draggable: false,
        lnglat: lngLatNew,
      })
    )
  }

  const handleLocationCapture = () =>
    !(
      Boolean(lngLatNew) &&
      popupLngLat?.[0]?.toString() !== lngLatNew?.[0] &&
      popupLngLat?.[1]?.toString() !== lngLatNew?.[1]
    )

  // Save the values of the current location
  useEffect(() => {
    window.localStorage.setItem('arbolQR_Map_Zoom', zoom)
    window.localStorage.setItem('arbolQR_Map_lng', lng)
    window.localStorage.setItem('arbolQR_Map_lat', lat)
    return () => {
      window.localStorage.removeItem('arbolQR_Map_Zoom')
      window.localStorage.removeItem('arbolQR_Map_lng')
      window.localStorage.removeItem('arbolQR_Map_lat')
    }
  }, [lng, lat, zoom])

  // Create map and geolocate
  useEffect(() => {
    // To change multiple maps, useEffect depends on styleMap,
    // otherwise it will be controlled by mapTree.current.
    if (!mapboxgl.supported()) {
      alert('Tu navegador no soporta Mapbox GL')
      return
    }

    mapTree.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: `mapbox://styles/mapbox/${_styleMap}`,
      center: [lng, lat],
      zoom,
      minZoom: 12,
      projection: 'globe',
      pitch: 30,
      maxPitch: 70,
    })

    geolocate.current = new mapboxgl.GeolocateControl({
      positionOptions: { enableHighAccuracy: false },
      showAccuracyCircle: location.pathname === mainRute,
      fitBoundsOptions: { maxZoom: 17 },
      showUserHeading: false,
      showUserLocation: true,
      trackUserLocation: true, // Change to true to update location to current position
    })
    mapTree.current.addControl(geolocate.current)

    return () => mapTree.current.remove()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_styleMap])

  // Update minZoom if is admin user
  useEffect(() => {
    if (allOption) mapTree.current.setMinZoom(3)
    else mapTree.current.setMinZoom(12)
  }, [_styleMap, allOption])

  // Center the map in the initial position
  useEffect(() => {
    if (!mapTree.current) return // wait for map to initialize
    mapTree.current.on('move', () => {
      setLng(mapTree.current.getCenter().lng.toFixed(4))
      setLat(mapTree.current.getCenter().lat.toFixed(4))
      setZoom(mapTree.current.getZoom().toFixed(2))
    })
  })

  // Load configuration of data sources and layers
  useEffect(() => {
    if (!mapTree.current) return // wait for map to initialize
    mapTree.current.on('load', () => {
      addSource('trees', _initialState.data)
      addLayer('trees')

      mapTree.current.on('click', 'clusters', e => {
        const features = mapTree.current.queryRenderedFeatures(e.point, {
          layers: ['clusters'],
        })
        const clusterId = features[0].properties.cluster_id
        mapTree.current
          .getSource('trees')
          .getClusterExpansionZoom(clusterId, (err, zoom) => {
            if (err) return
            mapTree.current.easeTo({
              center: features[0].geometry.coordinates,
              zoom: zoom,
            })
          })
      })

      mapTree.current.on('click', 'unclustered-point', e => {
        mapTree.current.flyTo({
          center: e.features[0].geometry.coordinates,
          duration: 2000,
          essential: true,
        }) // center point
        dispatch(openDrawerLeft(false))
        const coordinates = e.features[0].geometry.coordinates.slice()
        const treeId = e.features[0].properties.id
        const code = e.features[0].properties.code

        while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
          coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360
        }

        const passProps = { treeId, coordinates, code, allOption, isAuth, mapTree } //AllOptions si es Admin only
        dispatch(setEnableButton(false))
        setPropsContent(passProps)
        setPopupLngLat(coordinates)
      })
    })
  })

  // Create new content popup
  useEffect(() => {
    if (!propsContent) return
    if (location.pathname === registerRute) return
    const condition = _.startsWith(location.pathname, mainRute)
    const ElementContent = condition ? PopupContent : ContentOptions
    setContent(<ElementContent {...propsContent} />)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propsContent])

  // Create new market for tree location new
  useEffect(() => {
    if (location.pathname !== registerRute) return
    setPopupLngLat([lng, lat])
    setContent(<ContentMoveMarker coordinates={[lng, lat]} />)
    dispatch(
      setMarker({
        openMarker: true,
        draggable: true,
        lnglat: [lng, lat],
      })
    )
    dispatch(changePopup(true))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  // Enable popup if was created
  useEffect(() => {
    if (location.pathname === registerRute) return
    if (!content) return
    if (!propsContent) return
    if (!popupLngLat) return
    dispatch(
      setMarker({
        openMarker: false,
        draggable: false,
        lnglat: popupLngLat,
      })
    )
    dispatch(changePopup(true))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content])

  // -------------LOADING-----------------------
  useEffect(() => {
    if (!mapTree?.current) return // wait for map to initialize
    if (_initialState?.data?.features?.length) return

    setLoading(true)
    const timeout = setTimeout(() => {
      setTimeoutReached(true)
    }, 10000) // 10 segundos como límite

    getAllLocations()
      .then(geojson => {
        clearTimeout(timeout)
        setLoading(false)

        if (!geojson || !geojson.features?.length) {
          if (timeoutReached) {
            window.alert(
              'Es posible que los datos no hayan cargado o se encuentren disponibles en este momento. Se recomienda recargar la página.'
            )
          }
          return
        }

        dispatch(setFeature(geojson))
        mapTree.current.getSource('trees').setData(geojson)
      })
      .catch(err => {
        clearTimeout(timeout)
        setLoading(false)
        window.alert(
          'Es posible que los datos no hayan cargado o se encuentren disponibles en este momento. Se recomienda recargar la página.'
        )
        console.log('Error :>> ', err)
      })

    return () => clearTimeout(timeout)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapTree?.current])

  useEffect(() => {
    if (!mapTree.current) return // wait for map to initialize
    mapTree.current.on('mouseenter', 'clusters', () => {
      mapTree.current.getCanvas().style.cursor = 'pointer'
    })
    mapTree.current.on('mouseenter', 'unclustered-point', () => {
      mapTree.current.getCanvas().style.cursor = 'pointer'
    })
    mapTree.current.on('mouseleave', 'clusters', () => {
      mapTree.current.getCanvas().style.cursor = ''
    })
    mapTree.current.on('mouseleave', 'unclustered-point', () => {
      mapTree.current.getCanvas().style.cursor = ''
    })
  })

  // Create a new Marker
  useEffect(() => {
    if (!markerStatus.openMarker) return
    if (markerTree.current) return
    markerTree.current = new mapboxgl.Marker({
      draggable: true,
    })
      .setLngLat(markerStatus.lnglat)
      .addTo(mapTree.current)
  }, [markerStatus])

  // Close the marker
  useEffect(() => {
    if (markerStatus.openMarker) return
    if (!markerTree.current) return
    markerTree.current.remove()
    markerTree.current = null
  }, [markerStatus.openMarker])

  // Capture marker location when moving
  useEffect(() => {
    if (!markerTree.current) return
    if (markerStatus.draggable && !markerTree.current.isDraggable())
      markerTree.current.setDraggable(true)

    markerTree.current.on('dragend', () => {
      const lngltd = markerTree.current.getLngLat()
      const coordinates = [lngltd.lng.toString(), lngltd.lat.toString()]
      if (!_.isEqual(coordinates, lngLatNew)) setLngLatNew(coordinates)
    })
  })

  // Update geojson when filtering trees
  useEffect(() => {
    if (!filter) return
    if (popupOpen) dispatch(changePopup(false))
    alertRef?.current?.openAlert(true)
    mapTree?.current?.getSource('trees')?.setData(_initialState?.data)

    dispatch(setFilterState(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter])

  // Show alert if not exist idTree
  useEffect(() => {
    if (activeAlert?.noCode) {
      isntTreeId?.current?.openAlert(true)
      isntTreeId?.current?.goRute(`/${rt.TREE}`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAlert])

  return (
    <Box component="div" sx={styleBoxContainerMap}>
      <MapContainer ref={mapContainer} widthMap={widthMap}>
        <LoadingData loading={loading} />
        <div className="sidebar">
          Longitude: {lng} | Latitude: {lat} | Zoom: {zoom}
        </div>
        {lngLatNew ? (
          <div className="coordinates">
            Longitude: {lngLatNew?.[0]} <br />
            Latitude: {lngLatNew?.[1]}
          </div>
        ) : null}
        {popupLngLat && popupOpen && (
          <PopupContentContainer lngLat={popupLngLat} map={mapTree.current}>
            {content}
          </PopupContentContainer>
        )}
        {markerStatus.draggable && popupOpen && (
          <Box sx={styleBottonContainer}>
            <Botton
              text="Capturar ubicación"
              onClick={handleTreeLocation}
              startIcon={<PinDrop />}
              styleButton={styleDefaultBotton}
              disabled={handleLocationCapture()}
            />
          </Box>
        )}
        <AlertCustom ref={alertRef} severity="info">
          {_initialState.data?.features?.length
            ? `Hay ${_initialState.data?.features?.length} registros`
            : 'No hay resultados'}
        </AlertCustom>
        <AlertCustom ref={isntTreeId} severity="warning">
          El código ingresado en la URL no existe.
        </AlertCustom>
      </MapContainer>
      {minimap && (
        <Box sx={{ height: '100%', width: '50%' }}>
          {Boolean(Object.keys(_initialState.data).length) ? (
            <Outlet context={[setContent, setPopupLngLat, allOption, mapTree]} />
          ) : (
            <Box sx={contentStyle}>
              <LinearInderterminate />
              <Stack
                sx={{ color: 'green.600', alignItems: 'center', mt: '40vh' }}
                spacing={2}>
                <CircularProgress />
                <Box>Cargando...</Box>
              </Stack>
            </Box>
          )}
        </Box>
      )}
    </Box>
  )
}

export default Map
